import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Users, Sparkles, Shield, ArrowUpFromLine, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import TeamDetailModal from "@/components/app/TeamDetailModal";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const Section = ({ icon: Icon, tone = "green", title, subtitle, count, children }) => (
  <section className="totem-card overflow-hidden">
    <div className="flex items-center gap-3 p-4 border-b border-totem-borderSoft">
      <span
        className={cn(
          "grid place-items-center w-9 h-9 rounded-lg",
          tone === "green" && "bg-totem-green/15 text-totem-green",
          tone === "gold" && "bg-totem-gold/15 text-totem-gold",
          tone === "dim" && "bg-totem-surfaceHigh text-totem-textDim"
        )}
      >
        <Icon size={16} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="display-heading text-base leading-tight">{title}</div>
        <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5">
          {subtitle}
        </div>
      </div>
      <span
        className={cn(
          "mono text-xs uppercase font-semibold px-2 py-1 rounded-full",
          tone === "green" && "bg-totem-green/15 text-totem-green",
          tone === "gold" && "bg-totem-gold/15 text-totem-gold",
          tone === "dim" && "bg-totem-surfaceHigh text-totem-textDim"
        )}
      >
        {count}
      </span>
    </div>
    {children}
  </section>
);

function PlayerRow({ p, showOwnership = true, tone = "default" }) {
  return (
    <li>
      <Link
        to={`/players/${p.id}`}
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-totem-surfaceHigh transition"
      >
        <PlayerAvatar player={p} size={36} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{p.web_name}</div>
          <div className="mono text-[10px] uppercase text-totem-textDim">
            {p.team_short} • {p.position} • £{p.now_cost.toFixed(1)}m
          </div>
        </div>
        {showOwnership && (
          <div className="text-right">
            <div
              className={cn(
                "mono font-semibold",
                tone === "green" && "text-totem-green",
                tone === "gold" && "text-totem-gold",
                tone === "default" && "text-totem-text"
              )}
            >
              {p.ownership_pct}%
            </div>
            <div className="mono text-[9px] uppercase text-totem-textDim">
              {p.ownership_count}/{p.total_managers}
            </div>
          </div>
        )}
      </Link>
    </li>
  );
}

export default function LeagueDiff() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(false);
  const [selected, setSelected] = useState(null);
  const openManager = (mgr) =>
    setSelected({ entry: mgr.entry, entry_name: mgr.entry_name, player_name: mgr.player_name });
  const { data, isLoading, error } = useQuery({
    queryKey: ["league-diff", id],
    queryFn: async () => (await api.get(`/leagues/${id}/diff`)).data,
    staleTime: 5 * 60_000,
  });

  const buckets = useMemo(() => {
    if (!data?.players) return { yourDiff: [], missing: [], template: [] };
    const total = data.total_managers || 1;
    const players = data.players.map((p) => ({ ...p, total_managers: total }));

    const yourDiff = players.filter(
      (p) => p.owned_by_user && p.ownership_count <= Math.max(1, Math.floor(total * 0.3))
    );
    const missing = players.filter(
      (p) => !p.owned_by_user && p.ownership_count >= Math.max(2, Math.ceil(total * 0.4))
    );
    const template = players.filter(
      (p) => p.owned_by_user && p.ownership_count >= Math.max(2, Math.ceil(total * 0.6))
    );
    return { yourDiff, missing, template };
  }, [data]);

  if (isLoading) return <div className="totem-card h-72 animate-pulse mt-4" />;
  if (error)
    return (
      <div className="totem-card p-6 text-totem-red">
        Could not load rival diff — {String(error?.response?.data?.detail || error.message)}
      </div>
    );

  return (
    <div className="space-y-4 pt-1 animate-fade-up" data-testid={T.leagueDiffPage}>
      <Link
        to={`/leagues/${id}`}
        className="inline-flex items-center gap-1 text-totem-textDim hover:text-totem-green text-sm"
      >
        <ChevronLeft size={16} /> Standings
      </Link>

      <section className="totem-card p-5">
        <div className="label-caps mb-1">Rival diff</div>
        <h2 className="display-heading text-2xl">{data.league?.name}</h2>
        <div className="mono uppercase text-[10px] text-totem-textDim mt-1">
          GW{data.gameweek || "—"} • {data.total_managers || 0} managers sampled
        </div>
        {!data.user_in_league && data.total_managers > 0 && (
          <div className="mt-3 border border-totem-amber/40 bg-totem-amber/10 rounded-lg px-3 py-2 text-xs text-totem-amber">
            You&apos;re not in this league&apos;s top 30 — showing rival ownership only.
          </div>
        )}
      </section>

      {data.message ? (
        <section className="totem-card p-6 text-center border-totem-amber/30">
          <Sparkles className="mx-auto text-totem-amber mb-2" size={22} />
          <div className="display-heading text-lg mb-1">Pre-season</div>
          <p className="text-sm text-totem-textDim leading-relaxed">{data.message}</p>
        </section>
      ) : (
        <>
          <Section
            icon={ArrowUpFromLine}
            tone="green"
            title="Your differentials"
            subtitle="You own — rivals mostly don't"
            count={buckets.yourDiff.length}
          >
            {buckets.yourDiff.length === 0 ? (
              <div className="p-4 text-center text-xs text-totem-textDim">
                Nothing unique yet — your squad matches the league template.
              </div>
            ) : (
              <ul data-testid={T.leagueDiffYourDiff} className="divide-y divide-totem-borderSoft">
                {buckets.yourDiff.map((p) => (
                  <PlayerRow key={p.id} p={p} tone="green" />
                ))}
              </ul>
            )}
          </Section>

          <Section
            icon={Users}
            tone="gold"
            title="Missing rival template"
            subtitle="Most of the league owns — you don't"
            count={buckets.missing.length}
          >
            {buckets.missing.length === 0 ? (
              <div className="p-4 text-center text-xs text-totem-textDim">
                You&apos;re on the template — no popular picks missing.
              </div>
            ) : (
              <ul data-testid={T.leagueDiffMissing} className="divide-y divide-totem-borderSoft">
                {buckets.missing.map((p) => (
                  <PlayerRow key={p.id} p={p} tone="gold" />
                ))}
              </ul>
            )}
          </Section>

          <Section
            icon={Shield}
            tone="dim"
            title="Shared template"
            subtitle="You + the league on the same page"
            count={buckets.template.length}
          >
            {buckets.template.length === 0 ? (
              <div className="p-4 text-center text-xs text-totem-textDim">—</div>
            ) : (
              <ul className="divide-y divide-totem-borderSoft">
                {buckets.template.slice(0, 8).map((p) => (
                  <PlayerRow key={p.id} p={p} />
                ))}
              </ul>
            )}
          </Section>

          {/* Expandable full grid */}
          <button
            onClick={() => setShowGrid((v) => !v)}
            className="w-full totem-card p-4 flex items-center justify-between hover:border-totem-green/40 transition"
          >
            <span className="label-caps">
              Full ownership grid ({data.players.length} players × {data.total_managers} managers)
            </span>
            <ChevronDown
              size={16}
              className={cn("transition-transform text-totem-textDim", showGrid && "rotate-180")}
            />
          </button>

          {showGrid && (
            <section data-testid={T.leagueDiffGrid} className="totem-card p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-totem-surface sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 label-caps sticky left-0 bg-totem-surface min-w-[130px]">
                        Player
                      </th>
                      <th className="px-2 py-2 label-caps text-center">Own %</th>
                      {data.managers.map((m) => (
                        <th
                          key={m.entry}
                          title={`${m.entry_name} — ${m.player_name} (tap to view squad)`}
                          onClick={() => openManager(m)}
                          data-testid={`diff-grid-manager-${m.entry}`}
                          className={cn(
                            "px-1 py-2 mono text-[10px] uppercase text-totem-textDim min-w-[40px] cursor-pointer hover:text-totem-green transition",
                            m.entry === data.user_manager_id && "text-totem-green"
                          )}
                        >
                          {m.entry === data.user_manager_id
                            ? "YOU"
                            : `#${m.rank ?? "?"}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.players.slice(0, 60).map((p) => (
                      <tr key={p.id} className="border-t border-totem-borderSoft">
                        <td className="px-3 py-2 sticky left-0 bg-totem-surface min-w-[130px]">
                          <div className="flex items-center gap-2">
                            <PlayerAvatar player={p} size={22} />
                            <div className="min-w-0">
                              <div className="font-semibold truncate">{p.web_name}</div>
                              <div className="mono text-[9px] uppercase text-totem-textDim truncate">
                                {p.team_short}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center mono">{p.ownership_pct}%</td>
                        {data.managers.map((m) => {
                          const owned = p.owned_by_ids.includes(m.entry);
                          const isMe = m.entry === data.user_manager_id;
                          return (
                            <td
                              key={m.entry}
                              className={cn(
                                "text-center py-2 px-1 mono text-xs",
                                owned && isMe && "bg-totem-green/20 text-totem-green",
                                owned && !isMe && "bg-totem-gold/10 text-totem-gold",
                                !owned && "text-totem-textFaint"
                              )}
                            >
                              {owned ? "●" : "·"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <p className="text-[11px] text-totem-textDim leading-relaxed px-1">
            Sampled from the top {data.total_managers} managers in this league. Percentages are
            in-league ownership, not overall FPL ownership.
          </p>
        </>
      )}
      <TeamDetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        managerId={selected?.entry}
        entryName={selected?.entry_name}
        playerName={selected?.player_name}
      />
    </div>
  );
}
